import { useCallback, useEffect, useRef } from 'react';

import { aiAgentsApi, type SidekickMessage } from 'apis/aiAgents';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';

import { type MimUIMessage } from '../utils/sdk/structuredResponse';
import { prependOlder, toUIMessage } from '../utils/sidekickMessages';

const PAGE_SIZE = 50;

type LoadMode = 'replace' | 'prepend';

type SetMessages = (
  messages: MimUIMessage[] | ((messages: MimUIMessage[]) => MimUIMessage[]),
) => void;

type UseSidekickMessagesResult = {
  loadChat: (chatId: string) => void;
  loadOlder: () => void;
  hasOlder: boolean;
  isLoadingOlder: boolean;
};

export const useSidekickMessages = (
  setMessages: SetMessages,
): UseSidekickMessagesResult => {
  const loadModeRef = useRef<LoadMode>('replace');
  const appliedPageRef = useRef<readonly SidekickMessage[] | null>(null);

  const { data, asyncStatus, hasNextPage, isPaginating, nextPage, changeArg } =
    useLazyPaginatedEndpoint({
      endpoint: aiAgentsApi.endpoints.readAiAgentsSidekickConversationsMessages,
      itemsSelector: (response) => response.data.messages,
      totalSelector: (response) => response.meta.total,
    });

  const page = data.messages;

  useEffect(() => {
    if (!page || page === appliedPageRef.current) return;
    appliedPageRef.current = page;

    const loaded = page.map(toUIMessage);

    setMessages((current) =>
      loadModeRef.current === 'replace'
        ? loaded
        : prependOlder(loaded, current),
    );
  }, [page, setMessages]);

  const loadChat = useCallback(
    (conversationId: string) => {
      loadModeRef.current = 'replace';
      appliedPageRef.current = null;
      changeArg({
        conversationId,
        limit: PAGE_SIZE,
        offset: 0,
        anchor: undefined,
      });
    },
    [changeArg],
  );

  const loadOlder = useCallback(() => {
    if (!hasNextPage || isPaginating) return;
    loadModeRef.current = 'prepend';
    nextPage();
  }, [hasNextPage, isPaginating, nextPage]);

  return {
    loadChat,
    loadOlder,
    hasOlder: hasNextPage,
    isLoadingOlder: isPaginating || asyncStatus === 'pending',
  };
};
